import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Tooltip, IconButton, Typography } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import DateRangeFilter from "../components/common/DateRangeFilter";
import StandardTable, { TableColumn } from "../components/common/StandardTable";
import AppButton from "../components/buttons/AppButton";

import useDebounce from "../hooks/useDebounce";
import useWorkspaceNavigation from "../hooks/useWorkspaceNavigation";
import { usePermissions } from "../hooks/usePermissions";
import { getPurchaseOrders } from "../redux/actions/purchaseOrderActions";
import StatusChip from "../components/common/StatusChip";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { PurchaseOrderSummaryDto } from "../shared/dtos/purchaseOrder/PurchaseOrderSummaryDto";
import { formatPlainNumber } from "../utils/appUtils";

const PurchaseOrdersPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { navigateToTab } = useWorkspaceNavigation();
  const { hasPermission } = usePermissions();

  const today = new Date().toISOString().split("T")[0];

  const {
    items: orders,
    pagination,
    loadingFetchAll: loadingOrders,
    errorFetchAll: errorLoadingOrders,
  } = useAppSelector((state) => state.purchaseOrder);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);
  const [startDate, setStartDate] = useState<string>(today);
  const [endDate, setEndDate] = useState<string>(today);
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });

  const canRead = hasPermission("purchase_order:read_all");
  const canCreate = hasPermission("purchase_order:create");

  const fetchOrders = useCallback(() => {
    if (!canRead) return;
    dispatch(
      getPurchaseOrders({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch,
        startDate,
        endDate,
      }),
    );
  }, [dispatch, pageConfig, debouncedSearch, startDate, endDate, canRead]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    setPageConfig((p) => ({ ...p, page: 1 }));
  }, [debouncedSearch, startDate, endDate]);

  // --- Table Configuration with Column Management ---
  const columns: TableColumn<PurchaseOrderSummaryDto>[] = useMemo(
    () => [
      // --- DEFAULT VISIBLE COLUMNS ---
      {
        id: "poNumber",
        label: "PO #",
        minWidth: 120,
        format: (_, row) => <b style={{ color: "#4cc9f0" }}>{row.poNumber}</b>,
      },
      {
        id: "orderDate",
        label: "Order Date",
        minWidth: 120,
        format: (_, row) => new Date(row.orderDate).toLocaleDateString(),
      },
      { id: "supplierName", label: "Supplier", minWidth: 150 },
      {
        id: "status",
        label: "Status",
        minWidth: 120,
        format: (_, row) => <StatusChip status={row.status} />,
      },
      {
        id: "total", // Using an existing key, though the column renders a fixed string
        label: "CUR",
        minWidth: 60,
        align: "center",
        format: () => "KES", // Your original code ignored the value and hardcoded 'KES'
      },
      {
        id: "total",
        label: "Total Cost",
        minWidth: 120,
        align: "right",
        format: (_, row) => (
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {formatPlainNumber(row.total)}
          </Typography>
        ),
      },
      {
        id: "paidAmount",
        label: "Paid",
        minWidth: 120,
        align: "right",
        format: (_, row) => (
          <Typography
            variant="body2"
            sx={{ fontWeight: "bold", color: "success.main" }}
          >
            {formatPlainNumber(row.paidAmount)}
          </Typography>
        ),
      },

      // --- HIDDEN BY DEFAULT ---
      {
        id: "expectedDate",
        label: "Expected Date",
        minWidth: 120,
        defaultHidden: true,
        format: (_, row) =>
          row.expectedDate
            ? new Date(row.expectedDate).toLocaleDateString()
            : "N/A",
      },
      {
        id: "transactionStatus",
        label: "Txn Status",
        minWidth: 110,
        defaultHidden: true,
      },
    ],
    [],
  );

  const renderRowActions = (
    row: PurchaseOrderSummaryDto,
  ): React.JSX.Element => (
    <Tooltip title="View Purchase Order">
      <IconButton
        size="small"
        color="primary"
        onClick={() => navigateToTab(`/purchase-orders/${row.id}`)}
      >
        <VisibilityOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  return (
    <PageContainer
      title="Purchase Orders"
      subtitle="Manage supplier orders, receive goods, and track supplier payments."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => navigateToTab("/purchase-orders/create")}
          >
            Create Purchase Order
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search PO # or Supplier..."
      >
        <DateRangeFilter
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
      </PageToolbar>
      <StandardTable
        columns={columns}
        data={orders}
        loading={loadingOrders}
        error={errorLoadingOrders}
        onRetry={fetchOrders}
        page={pageConfig.page}
        count={pagination?.totalElements || 0}
        rowsPerPage={pageConfig.limit}
        onPageChange={(p) => setPageConfig({ ...pageConfig, page: p })}
        onRowsPerPageChange={(l) =>
          setPageConfig({ ...pageConfig, limit: l, page: 1 })
        }
        enableColumnManagement={true}
        rowAction={renderRowActions}
      />
    </PageContainer>
  );
};

export default PurchaseOrdersPage;
